import { useCallback, useMemo, useState } from 'react';

import { safeCall } from '@xemplo/safe-call';
import { ListValue } from '@xemplo/stateful-list-core';

export type UseValueTrackerProps = {
  /** The controlled value (when provided, component is controlled) */
  value?: ListValue[];
  /** Callback when selection changes */
  onChange?: (selected: ListValue[]) => void;
};

export type UseValueTrackerReturn = {
  /** The current selected items as a Set */
  selectedItems: Set<ListValue>;
  /** Whether component is in controlled mode */
  isControlled: boolean;
  /** Check if a value is selected */
  isSelected: (value: ListValue) => boolean;
  /** Toggle a single item's selection */
  toggleItem: (
    value: ListValue,
    valueItemMap: Map<ListValue, { itemCallback?: (value: ListValue) => void }>
  ) => void;
  /** Update selection with a new Set (for batch operations like select all) */
  updateSelection: (newSet: Set<ListValue>) => void;
};

/**
 * Hook that manages controlled/uncontrolled selection state for list components.
 * When `value` prop is provided, operates in controlled mode.
 * When `value` is undefined, manages internal state (uncontrolled mode).
 */
export function useValueTracker({
  value,
  onChange,
}: UseValueTrackerProps): UseValueTrackerReturn {
  const [internalSelected, setInternalSelected] = useState<Set<ListValue>>(
    () => new Set(value ?? [])
  );

  const { isControlled, selectedItems } = useMemo(() => {
    const isControlled = value !== undefined;
    const selectedItems = isControlled ? new Set(value) : internalSelected;
    return { isControlled, selectedItems };
  }, [value, internalSelected]);

  const isSelected = useCallback(
    (value: ListValue) => selectedItems.has(value),
    [selectedItems]
  );

  const updateSelection = useCallback(
    (newSet: Set<ListValue>) => {
      if (!isControlled) {
        setInternalSelected(newSet);
      }
      onChange?.(Array.from(newSet));
    },
    [isControlled, onChange]
  );

  const toggleItem = useCallback(
    (
      value: ListValue,
      valueItemMap: Map<ListValue, { itemCallback?: (value: ListValue) => void }>
    ) => {
      const [, error] = safeCall(() => {
        const newSet = new Set(selectedItems);
        if (newSet.has(value)) {
          newSet.delete(value);
        } else {
          newSet.add(value);
        }

        const item = valueItemMap.get(value);
        if (!item) {
          console.warn(`Item with value "${value}" not found in the list.`);
          return;
        }

        updateSelection(newSet);
      });

      if (error) {
        console.error('Error when toggling item:', error);
      }
    },
    [selectedItems, updateSelection]
  );

  return {
    selectedItems,
    isControlled,
    isSelected,
    toggleItem,
    updateSelection,
  };
}
